import Vue from 'vue'
import Vuex from 'vuex'
import generateGrid from '../lib/filzfelt/generate-pattern'
import generateSpGrid from '../lib/spinneybeck/generate-pattern'
import spPatterns from '../../universal/spinneybeck/patterns'
import ffPatterns from '../../universal/filzfelt/patterns'
import SP_BLOCKS from '../../universal/spinneybeck/blocks'
import getSp402Variant from '../lib/spinneybeck/get-sp402-variant'
import router from '../router'
import * as api from '../lib/api'
import { DEFAULT_WALL_HEIGHT, DEFAULT_WALL_WIDTH } from '../lib/constants';

const generate = (id, ...args) => {
  if (id in ffPatterns) return generateGrid(id, ...args)
  if (id in spPatterns) return generateSpGrid(id, ...args)
}

const toCentimeters = value => Math.round(value * 2.54)

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    brand: 'filzfelt',
    design: {
      pattern: null,
      width: DEFAULT_WALL_WIDTH,
      height: DEFAULT_WALL_HEIGHT,
      data: []
    },
    metric: false,
    email: null,
    authorized: false,
    secret: null,
    saveError: null,
    saveSuccess: null
  },

  getters: {
    brand (state) {
      if (state.route.params.brand) return state.route.params.brand
      const p = state.design.pattern
      if (p in ffPatterns) return 'filzfelt'
      if (p in spPatterns) return 'spinneybeck'
      return 'filzfelt'
    },
    metric (state) {
      return state.metric
    },
    design (state) {
      return state.design
    },
    width (state) {
      if (state.metric) {
        return `${toCentimeters(state.design.width)} cm`
      }
      let inches = state.design.width.toFixed(0)
      const feet = Math.floor(inches / 12)
      return inches && inches > 0
        ? `${feet}'-${inches % 12}"`
        : `${feet}'`
    },
    height (state) {
      if (state.metric) {
        return `${toCentimeters(state.design.height)} cm`
      }
      let inches = state.design.height.toFixed(0)
      const feet = Math.floor(inches / 12)
      return inches && inches > 0
        ? `${feet}'-${inches % 12}"`
        : `${feet}'`
    },

    // sp402BlockInUse (state) {
    //   const blockId = {
    //     'aff5ea63-34c8-40f0-8c65-a510460ba076': 'ea6a6f31-ea62-4cbe-9705-f104dddfbcf9',
    //     '281d02c2-e7e5-4252-9e7b-2068a366fc01': '4a64a548-bcbb-48d6-8c60-86af1371d164',
    //     'cd9ca5e5-ed19-4729-812c-8182ab8fc5c7': '3eadae5c-c35d-4f74-a516-a9e0795ac76f'
    //   }[state.design.pattern]
    //   return SP_BLOCKS[blockId]
    // },

    sp402Pattern (state, getters) {
      const BLOCK = getters.sp402BlockInUse
      if (!BLOCK) return
      const data = []
      for (const key of Object.keys(state.design.data)) {
        const block = state.design.data[key]
        const variant = getSp402Variant(state.design.data, key)
        if (variant) {
          const path = BLOCK.variants[variant]
          data.push({ ...block, key, path })
        }
      }
      return data
    }
  },

  mutations: {
    setBrand (state, brand) {
      state.brand = brand
    },
    setDesignData (state, design) {
      for (const key of ['data', 'pattern', 'width', 'height']) {
        state.design[key] = design[key]
      }
    },
    setPattern (state, pattern) {
      const { pattern: oldPattern, width, height, data } = state.design
      state.design.data = generate(pattern, width, height, pattern === oldPattern ? data : undefined)
      state.design.pattern = pattern
    },
    setWidth (state, width) {
      const { pattern, height, data } = state.design
      state.design.width = width || 0
      state.design.data = generate(pattern, width, height, data)
    },
    setHeight (state, height) {
      const { pattern, width, data } = state.design
      state.design.height = height || 0
      state.design.data = generate(pattern, width, height, data)
    },
    setColor (state, { key, color }) {
      const block = state.design.data[key]
      if (!block) return
      if (block.color !== color) {
        Vue.set(block, 'color', color)
      } else {
        Vue.delete(block, 'color')
      }
    },
    setColorForSameBlockType (state, { key, color }) {
      for (const block of Object.values(state.design.data)) {
        if (block.id === state.design.data[key].id) {
          Vue.set(block, 'color', color)
        }
      }
    },
    setColorForAll (state, color) {
      for (const block of Object.values(state.design.data)) {
        Vue.set(block, 'color', color)
      }
    },
    clearColorForAll (state) {
      for (const block of Object.values(state.design.data)) {
        Vue.delete(block, 'color')
      }
    },
    setCustomBlock (state, { key, id, name, r }) {
      const block = state.design.data[key]
      if (!block) return
      Vue.set(block, 'id', id)
      Vue.set(block, 'name', name)
      Vue.set(block, 'r', r)
    },
    setBlockRotate (state, { key, r }) {
      const block = state.design.data[key]
      if (!block) return
      Vue.set(block, 'r', r)
    },
    setEmail (state, email) {
      state.email = email
    },
    setSecret (state, secret) {
      state.secret = secret
    },
    setAuthorized (state, authorized) {
      state.authorized = authorized
    },
    setSaveError (state, saveError) {
      state.saveError = saveError
    },
    setSaveSuccess (state, saveSuccess) {
      state.saveSuccess = saveSuccess
    },
    setMetric (state, metric) {
      state.metric = metric
    }
  },

  actions: {
    async save ({ commit, state }) {
      const { design, email, secret, brand } = state
      const { id, slug } = state.route.params
      try {
        if (id && !slug) {
          // if no slug, assume it's an exisiting design
          await api.designs.update(id, { design, secret })
        } else {
          // otherwise, save new custom design from a pattern
          const { id } = await api.designs.create({ design, email, secret, brand })
          router.push({ params: { id, slug: null } })
        }
        commit('setAuthorized', true)
        commit('setSaveSuccess', 'Design updated.')
        setTimeout(() => commit('setSaveSuccess', null), 3000)
        commit('setSaveError', null)
      } catch (err) {
        if (err.response.status === 403) {
          commit('setSaveError', 'Incorrect secret.')
        } else {
          throw err
        }
      }
    },

    setPatternById ({ commit, state }, id) {
      let slug = null
      if (state.brand === 'filzfelt') {
        slug = ffPatterns[id].name
      } else if (state.brand === 'spinneybeck') {
        slug = spPatterns[id].name
      }

      if (!id || !slug) return

      commit('setPattern', id)
      // update path to current pattern
      router.push({ params: { id, slug } })
    }
  }
})



// WEBPACK FOOTER //
// ./src/client/store/index.js